"use client";

import { useMemo, useState, type ReactNode } from "react";
import { Popover, usePopover } from "./popover.tsx";
import { CalendarIcon, ChevronIcon, ClockIcon } from "./icons.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Dates and times.

   `<input type="date">` renders three different controls in three browsers,
   none of which can be told what the house colours are, and Safari's is a
   stepper with no calendar at all. So: one calendar, drawn here, keyboard
   navigable, and the same one everywhere.

   Everything crossing an API boundary is an ISO instant. Everything a person
   sees is local. The two are converted at exactly these edges and nowhere
   else, which is why scheduling a link for 9am does not become 8am in
   November.
   ────────────────────────────────────────────────────────────────────────── */

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

export function toLocalDate(iso: string | null): Date | null {
  if (!iso) return null;
  // Signet, 2026-09-28: a bare date is a calendar day, not a moment. Parsed as
  // ISO it becomes UTC midnight, which is the previous day anywhere west of
  // Greenwich, so the picker showed yesterday there.
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function toISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatDate(iso: string | null, options?: Intl.DateTimeFormatOptions): string {
  const date = toLocalDate(iso);
  if (!date) return "—";
  return date.toLocaleDateString(undefined, options ?? { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(iso: string | null): string {
  const date = toLocalDate(iso);
  if (!date) return "—";
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "3 minutes ago", "in 2 days". Used everywhere a timestamp is a fact about recency. */
export function relativeTime(iso: string | null, now = Date.now()): string {
  const date = toLocalDate(iso);
  if (!date) return "—";
  const delta = date.getTime() - now;
  const abs = Math.abs(delta);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31536000000],
    ["month", 2592000000],
    ["week", 604800000],
    ["day", 86400000],
    ["hour", 3600000],
    ["minute", 60000],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, size] of units) {
    if (abs >= size) return formatter.format(Math.round(delta / size), unit);
  }
  return "just now";
}

/* ── the calendar ──────────────────────────────────────────────────────── */

function Calendar({
  selected,
  rangeEnd,
  onPick,
  min,
  max,
}: {
  selected: Date | null;
  rangeEnd?: Date | null;
  onPick: (date: Date) => void;
  min?: Date | null;
  max?: Date | null;
}) {
  const [month, setMonth] = useState(() => {
    const base = selected ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const cells = useMemo(() => {
    const year = month.getFullYear();
    const index = month.getMonth();
    const days = new Date(year, index + 1, 0).getDate();
    const offset = new Date(year, index, 1).getDay();
    return Array.from({ length: offset + days }, (_, cell) =>
      cell < offset ? null : new Date(year, index, cell - offset + 1)
    );
  }, [month]);

  const today = toISODate(new Date());
  const selectedIso = selected ? toISODate(selected) : null;
  const endIso = rangeEnd ? toISODate(rangeEnd) : null;

  const step = (delta: number) => setMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1));

  return (
    <div className="calendar">
      <div className="calendar-topline">
        <div>
          <span className="eyebrow">Pick a date</span>
          <strong>{month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</strong>
        </div>
        <div className="calendar-arrows">
          <button type="button" className="icon-button small" onClick={() => step(-1)} aria-label="Previous month">
            <ChevronIcon size={12} direction="left" />
          </button>
          <button type="button" className="icon-button small" onClick={() => step(1)} aria-label="Next month">
            <ChevronIcon size={12} direction="right" />
          </button>
        </div>
      </div>

      <div className="weekdays" aria-hidden="true">
        {WEEKDAYS.map((day, index) => (
          <span key={`${day}${index}`}>{day}</span>
        ))}
      </div>

      <div
        className="calendar-grid"
        role="grid"
        onKeyDown={(event) => {
          const map: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
          const delta = map[event.key];
          if (delta === undefined) return;
          event.preventDefault();
          const base = selected ?? new Date();
          const next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + delta);
          setMonth(new Date(next.getFullYear(), next.getMonth(), 1));
          onPick(next);
        }}
      >
        {cells.map((day, index) => {
          if (!day) return <span key={`blank${index}`} />;
          const iso = toISODate(day);
          const disabled = Boolean((min && iso < toISODate(min)) || (max && iso > toISODate(max)));
          const inRange = Boolean(selectedIso && endIso && iso > selectedIso && iso < endIso);
          return (
            <button
              key={iso}
              type="button"
              disabled={disabled}
              className={[
                iso === selectedIso || iso === endIso ? "selected" : "",
                inRange ? "in-range" : "",
                iso === today ? "today" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              aria-label={day.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
              aria-current={iso === today ? "date" : undefined}
              onClick={() => onPick(day)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── date and time picker ──────────────────────────────────────────────── */

export function DateTimePicker({
  value,
  onChange,
  label,
  placeholder = "Not set",
  withTime = true,
  min,
  clearable = true,
  compact = false,
  renderValue,
  className = "",
}: {
  /** ISO instant, or null. */
  value: string | null;
  /** What the closed picker shows instead of the formatted date, such as "Due today". */
  renderValue?: (value: string | null) => ReactNode;
  className?: string;
  onChange: (value: string | null) => void;
  label: string;
  placeholder?: string;
  withTime?: boolean;
  min?: string | null;
  clearable?: boolean;
  compact?: boolean;
}) {
  const { anchorRef, open, setOpen, close } = usePopover();
  const current = toLocalDate(value);
  const [hour, setHour] = useState(() => current?.getHours() ?? 9);
  const [minute, setMinute] = useState(() => current?.getMinutes() ?? 0);

  const emit = (date: Date, h = hour, m = minute) => {
    const next = new Date(date);
    if (withTime) next.setHours(h, m, 0, 0);
    else next.setHours(0, 0, 0, 0);
    onChange(next.toISOString());
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`combobox-trigger${compact ? " compact" : ""} ${className}`.trim()}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(!open)}
      >
        <span style={{ display: "grid", placeItems: "center", marginLeft: 5, color: "var(--faint)" }}>
          <CalendarIcon size={13} />
        </span>
        <span className={`combobox-value${value ? "" : " placeholder"}`}>
          <strong>{renderValue ? renderValue(value) : value ? (withTime ? formatDateTime(value) : formatDate(value)) : placeholder}</strong>
        </span>
        <ChevronIcon size={12} className="combobox-chevron" />
      </button>

      <Popover anchorRef={anchorRef} open={open} onClose={close} label={label} minWidth={withTime ? 400 : 262}>
        <div style={{ display: "flex", minWidth: 0 }}>
          <Calendar selected={current} onPick={(date) => emit(date)} min={min ? toLocalDate(min) : null} />
          {withTime ? (
            <div
              style={{
                borderLeft: "1px solid var(--line-soft)",
                padding: 12,
                width: 138,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <span className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <ClockIcon size={11} /> Time
              </span>
              <div className="time-grid">
                <div className="time-col" role="listbox" aria-label="Hour">
                  {Array.from({ length: 24 }, (_, h) => (
                    <button
                      key={h}
                      type="button"
                      role="option"
                      aria-selected={h === hour}
                      className={h === hour ? "on" : ""}
                      onClick={() => {
                        setHour(h);
                        emit(current ?? new Date(), h, minute);
                      }}
                    >
                      {String(h).padStart(2, "0")}
                    </button>
                  ))}
                </div>
                <div className="time-col" role="listbox" aria-label="Minute">
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                    <button
                      key={m}
                      type="button"
                      role="option"
                      aria-selected={m === minute}
                      className={m === minute ? "on" : ""}
                      onClick={() => {
                        setMinute(m);
                        emit(current ?? new Date(), hour, m);
                      }}
                    >
                      {String(m).padStart(2, "0")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="pop-foot">
          {clearable ? (
            <button
              type="button"
              className="text-button tiny"
              onClick={() => {
                onChange(null);
                close();
              }}
            >
              Clear
            </button>
          ) : (
            <span />
          )}
          <div className="row tight">
            <button
              type="button"
              className="text-button tiny"
              onClick={() => {
                emit(new Date());
              }}
            >
              Now
            </button>
            <button type="button" className="button brass tiny" onClick={close}>
              Done
            </button>
          </div>
        </div>
      </Popover>
    </>
  );
}

/* ── date range ────────────────────────────────────────────────────────── */

export type DateRange = { from: string | null; to: string | null };

export function DateRangePicker({
  value,
  onChange,
  label = "Date range",
  presets = true,
  defaultLabel,
}: {
  value: DateRange;
  onChange: (value: DateRange) => void;
  label?: string;
  presets?: boolean;
  /** Shown when no explicit range is set — what the caller actually defaults to. */
  defaultLabel?: string;
}) {
  const { anchorRef, open, setOpen, close } = usePopover();
  const [draft, setDraft] = useState<DateRange>(value);

  const from = toLocalDate(draft.from);
  const to = toLocalDate(draft.to);

  // First click sets the start, second sets the end, and a date earlier than
  // the current start begins again rather than making a backwards range.
  const pick = (date: Date) => {
    const iso = toISODate(date);
    if (!draft.from || draft.to) {
      setDraft({ from: iso, to: null });
      return;
    }
    if (iso < draft.from) {
      setDraft({ from: iso, to: null });
      return;
    }
    setDraft({ from: draft.from, to: iso });
  };

  const applyPreset = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days + 1);
    const next = { from: toISODate(start), to: toISODate(end) };
    setDraft(next);
    onChange(next);
    close();
  };

  // Says what is actually being shown. "All time" on a control whose default
  // is the last thirty days is a label that contradicts the figures beside it.
  const shown =
    value.from && value.to
      ? `${formatDate(value.from, { day: "numeric", month: "short" })} → ${formatDate(value.to, { day: "numeric", month: "short" })}`
      : (defaultLabel ?? "All time");

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className="secondary-button compact"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setDraft(value);
          setOpen(!open);
        }}
      >
        <CalendarIcon size={12} />
        {shown}
        <ChevronIcon size={11} />
      </button>

      <Popover anchorRef={anchorRef} open={open} onClose={close} label={label} minWidth={262}>
        {presets ? (
          <div className="pop-options" style={{ borderBottom: "1px solid var(--line-soft)" }}>
            {[
              { label: "Last 7 days", days: 7 },
              { label: "Last 30 days", days: 30 },
              { label: "Last 90 days", days: 90 },
            ].map((preset) => (
              <button key={preset.days} type="button" onClick={() => applyPreset(preset.days)}>
                <span />
                <span className="pop-option-copy">
                  <strong>
                    <span>{preset.label}</span>
                  </strong>
                </span>
                <span />
              </button>
            ))}
          </div>
        ) : null}

        <div className="calendar" style={{ paddingBottom: 4 }}>
          <div className="selected-range">
            <span>{draft.from ? formatDate(draft.from, { day: "numeric", month: "short" }) : "Start"}</span>
            <span className="range-line" />
            <span>{draft.to ? formatDate(draft.to, { day: "numeric", month: "short" }) : "End"}</span>
          </div>
        </div>
        <Calendar selected={from} rangeEnd={to} onPick={pick} />

        <div className="pop-foot">
          <button
            type="button"
            className="text-button tiny"
            onClick={() => {
              onChange({ from: null, to: null });
              close();
            }}
          >
            All time
          </button>
          <button
            type="button"
            className="button brass tiny"
            disabled={!draft.from || !draft.to}
            onClick={() => {
              onChange(draft);
              close();
            }}
          >
            Apply
          </button>
        </div>
      </Popover>
    </>
  );
}
